import { ReactNode, useEffect, useMemo, useState } from "react";

import {
    AbfLoanExpanded,
    BsMetaUtil,
    getHealthStatus,
    getZcCollateral,
    Loan,
    loanCollateralGroupTableKey,
    LoanRowHealth,
    LoopPositionExpanded,
    StrategyExpandedInfo,
    TokenListMetadata
} from "@bridgesplit/abf-react";
import {
    Button,
    Column,
    FONT_SIZES,
    Icon,
    IconButton,
    MEDIA,
    MenuItemProps,
    MenuItems,
    PopoverWrapper,
    Row,
    SectionedMenuItems,
    SPACING,
    TagText,
    Text,
    useAppPalette,
    usePopover,
    Tooltip,
    TextVariant,
    TextColor,
    BORDER_RADIUS_SM,
    TagColor
} from "@bridgesplit/ui";
import {
    EMPTY_STRING,
    formatAddress,
    formatPercent,
    formatUsd,
    lamportsToUiAmount,
    SortOrder,
    USD_COMPACT_THRESHOLD
} from "@bridgesplit/utils";
import { ChunkedPaginationState } from "app/api/shared";
import { Box, useMediaQuery } from "@mui/material";
import { Visibility } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { AppDialog, useAppDialog } from "app/utils";

import { AppButton, TokenImage, TokenSize, getLoanAssetPath, useHealthColor } from "../common";
import { handleLoanAction, useLoanActionMenuItemSections } from "../loan/actions/util";
import { useLoanActions } from "../loan/side-card/CardLoanActionsButton";
import { LoanAction } from "../loan/side-card/types";
import { useStrategyActionMenuItemSections } from "../strategy/common";
import { useLoopSocialShareProps } from "./social-share";

export const EMPTY_CELL = () => <div />;

export const GROUP_CTA_CELL = () => <Box sx={{ width: 26, minWidth: 26, display: "block" }} />;

export const CELL_WIDTH = {
    xs: "4%",
    sm: "8%",
    md: "12%",
    lg: "16%"
};

export const CELL_WIDTH_DEFAULTS = {
    cta: CELL_WIDTH.xs,
    apy: CELL_WIDTH.md,
    default: CELL_WIDTH.sm,
    pairValue: CELL_WIDTH.lg,
    health: CELL_WIDTH.md
};

export const GROUP_HEADER_CELL_TOOLTIPS = {
    vaults: {
        apy: "Aggregate APYs display a average APY weighted by position size"
    },
    loops: {
        apy: "Aggregate APY display a average APY weighted by net position size"
    },
    loans: {
        apy: "Aggregate APYs display a average APY weighted by borrow amount"
    },
    strategies: {
        apy: "Aggregate APYs display a average APY weighted by position size"
    }
};

export const getDefaultPaginationController = <T, K extends { page: number; pageSize: number }>(
    pagination: ChunkedPaginationState<T, K>
) => {
    return {
        page: pagination.page,
        setPage: pagination.setPage,
        order: SortOrder.Desc,
        orderBy: EMPTY_STRING,
        resetPage: pagination.resetPagination
    };
};

export function loanMergePairKey(loan: AbfLoanExpanded | undefined): string | undefined {
    const principalMint = loan?.principalMetadata?.mint;
    const collateralKey = loan ? loanCollateralGroupTableKey(loan) : undefined;
    if (!principalMint || !collateralKey) return undefined;
    return `${principalMint}|${collateralKey}`;
}

export function buildMergeKeyByAddress<T>(
    grouped: Map<string, T[]> | undefined,
    getLoanExpanded: (item: T) => AbfLoanExpanded | undefined
): Map<string, string> | undefined {
    if (!grouped) return undefined;
    const map = new Map<string, string>();
    grouped.forEach((items) =>
        items.forEach((item) => {
            const loanExpanded = getLoanExpanded(item);
            const address = loanExpanded?.loan.address;
            const key = loanMergePairKey(loanExpanded);
            if (address && key) map.set(address, key);
        })
    );
    return map;
}

export function getMergeDisabledReason(selectedMergeGroupCount: number, term: string): string | undefined {
    return selectedMergeGroupCount > 1 ? `Select ${term} with the same collateral and principal to merge` : undefined;
}

export function useSelectedMergeGroupCount(
    selectedLoanAddresses: string[],
    keyByAddressPage: Map<string, string> | undefined
): number {
    const [keyByAddress, setKeyByAddress] = useState<Map<string, string>>(new Map());

    useEffect(() => {
        if (!keyByAddressPage) return;
        setKeyByAddress((prev) => {
            const next = new Map(prev);
            let changed = false;
            keyByAddressPage.forEach((key, address) => {
                if (next.get(address) !== key) {
                    next.set(address, key);
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [keyByAddressPage]);

    return useMemo(() => {
        const keys = new Set<string>();
        for (const address of selectedLoanAddresses) {
            const key = keyByAddress.get(address);
            if (key) keys.add(key);
        }
        return keys.size;
    }, [selectedLoanAddresses, keyByAddress]);
}

export type BulkAction = {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
    disabled?: boolean;
    errorTooltip?: string;
};

export type BulkActionsButtonProps = {
    count: number;
    size?: "small" | "medium";
    disabled?: boolean;
    actions: BulkAction[];
};

export function BulkActionsButton({ count, size = "small", disabled: disabledProp, actions }: BulkActionsButtonProps) {
    const { textDisabled } = useAppPalette();
    const popover = usePopover();
    const noSelection = count === 0;

    // 1 action → single direct-action button; 2+ → dropdown (only the dropdown shows a chevron).
    if (actions.length <= 1) {
        const action = actions[0];
        const disabled = (disabledProp ?? (noSelection || !!action?.disabled)) || !!action?.errorTooltip;
        return (
            <AppButton
                onClick={action?.onClick}
                variant="outlined"
                size={size}
                disabled={disabled}
                tooltip={action?.errorTooltip}
                isTransaction={false}
            >
                <Row spacing={1} sx={{ alignItems: "center", px: 1.5 }}>
                    {!!action?.errorTooltip && (
                        <Text color="error">
                            <Icon type="warning" />
                        </Text>
                    )}
                    <Text variant="body1" color="caption">
                        {action?.label}
                    </Text>
                    <BulkActionsCountTag count={count} />
                </Row>
            </AppButton>
        );
    }

    const disabled = disabledProp ?? noSelection;
    const dropdownItems: MenuItemProps[] = actions.map((action) => ({
        text: action.label,
        icon: action.icon,
        onClick: action.onClick,
        disabled: action.disabled || noSelection
    }));

    return (
        <>
            <Button
                variant="outlined"
                size={size}
                width="max-content"
                onClick={(event) => popover.open(event)}
                disabled={disabled}
            >
                <Row spacing={1} sx={{ alignItems: "center" }}>
                    <Text variant="body1" color="caption">
                        Bulk actions
                    </Text>
                    <BulkActionsCountTag count={count} />
                    <Icon type="chevron-down" sx={{ height: 20, width: 20, color: `${textDisabled} !important` }} />
                </Row>
            </Button>
            <PopoverWrapper
                transformOrigin={{ vertical: -1 * SPACING, horizontal: SPACING * 2 }}
                {...popover.popoverProps}
            >
                <MenuItems close={popover.close} items={dropdownItems} />
            </PopoverWrapper>
        </>
    );
}

function BulkActionsCountTag({ count }: { count: number }) {
    return (
        <TagText
            color={count === 0 ? "body" : "secondary"}
            borderRadius={100}
            textVariant="caption"
            sx={{ minWidth: 14, justifyContent: "center", lineHeight: 1, px: 1 }}
        >
            {count}
        </TagText>
    );
}

export function TableCell({
    value,
    tooltip
}: {
    value?: string | number | undefined;
    tooltip?: string | React.ReactNode;
}) {
    return (
        <Tooltip title={tooltip}>
            <Text variant="body1" translate="no">
                {value}
            </Text>
        </Tooltip>
    );
}

export function MissingValueCell() {
    return <TableCell value="-" />;
}

export function PairTokenValueCell(
    props: Omit<
        PairTokenValueTextProps,
        | "primaryVariant"
        | "primaryColor"
        | "secondaryVariant"
        | "secondaryColor"
        | "tokenImageSize"
        | "spacing"
        | "loading"
        | "loadingWidth"
    >
) {
    return <PairValueTextWithTooltip {...props} loading={false} />;
}

type PairTokenValueTextProps = {
    usdAmount?: number | undefined;
    tokenAmount?: number | undefined;
    metadata?: TokenListMetadata | undefined;
    isRawTokenAmount?: boolean;
    primary?: "token" | "usd";
    primaryVariant?: TextVariant;
    primaryColor?: TextColor;
    secondaryVariant?: TextVariant;
    secondaryColor?: TextColor;
    showTokenImage?: "start" | "replace";
    tokenImageSize?: TokenSize;
    spacing?: number;
    loading: boolean;
    loadingWidth?: string;
    primarySignDisplay?: boolean;
    secondarySignDisplay?: boolean;
};

export function PairValueTextWithTooltip({
    usdAmount,
    tokenAmount,
    metadata,
    isRawTokenAmount,
    primary = "token",
    primaryVariant = "body1",
    primaryColor = "body",
    secondaryVariant = "body2",
    secondaryColor = "caption",
    showTokenImage,
    tokenImageSize = "sm",
    spacing = 1,
    loading,
    loadingWidth,
    primarySignDisplay,
    secondarySignDisplay
}: PairTokenValueTextProps) {
    const { background } = useAppPalette();
    const isPrimaryUsd = primary === "usd" || tokenAmount === undefined;

    const condenseUsdValue = usdAmount && usdAmount > USD_COMPACT_THRESHOLD;
    const usdDisplayValue = formatUsd(usdAmount, {
        signDisplay: isPrimaryUsd ? primarySignDisplay : secondarySignDisplay
    });
    const usdTooltip = formatUsd(usdAmount, {
        showFullUsdValue: true,
        signDisplay: isPrimaryUsd ? primarySignDisplay : secondarySignDisplay
    });

    const tokenValue = isRawTokenAmount ? lamportsToUiAmount(tokenAmount, metadata?.decimals) : tokenAmount;
    const condenseTokenValue = tokenValue && tokenValue > USD_COMPACT_THRESHOLD;
    const tokenDisplayValue = BsMetaUtil.formatAmount(metadata, tokenValue, {
        hideSymbol: showTokenImage === "replace",
        shortSymbol: true,
        compact: true,
        signed: isPrimaryUsd ? secondarySignDisplay : primarySignDisplay
    });
    const tokenTooltip = BsMetaUtil.formatAmount(metadata, tokenValue, {
        signed: isPrimaryUsd ? secondarySignDisplay : primarySignDisplay
    });

    const [tokenVariant, tokenColor] = isPrimaryUsd
        ? [secondaryVariant, secondaryColor]
        : [primaryVariant, primaryColor];
    const [usdVariant, usdColor] = isPrimaryUsd ? [primaryVariant, primaryColor] : [secondaryVariant, secondaryColor];

    const TooltipContent = () => (
        <Row spacing={1} spaceBetween>
            <Text variant="caption" sx={{ color: background }}>
                {isPrimaryUsd ? usdTooltip : usdTooltip}
            </Text>
            {usdAmount !== undefined && tokenAmount !== undefined && (
                <Text variant="caption" color="caption">
                    {isPrimaryUsd ? tokenTooltip : usdTooltip}
                </Text>
            )}
        </Row>
    );

    const showTooltip =
        (usdAmount !== undefined && condenseUsdValue) || (tokenAmount !== undefined && condenseTokenValue);

    return (
        <Tooltip title={showTooltip ? <TooltipContent /> : undefined} tooltipSx={{ borderRadius: BORDER_RADIUS_SM }}>
            <Row spacing={spacing} direction={isPrimaryUsd ? "row" : "row-reverse"}>
                <Text variant={usdVariant} color={usdColor} loading={loading} loadingWidth={loadingWidth}>
                    {usdDisplayValue}
                </Text>
                <Row spacing={0.5}>
                    {showTokenImage === "start" && <TokenImage size={tokenImageSize} metadata={metadata} />}
                    {tokenValue !== undefined && (
                        <Text variant={tokenVariant} color={tokenColor} loading={loading} loadingWidth={loadingWidth}>
                            {tokenDisplayValue}
                        </Text>
                    )}
                    {showTokenImage === "replace" && <TokenImage size={tokenImageSize} metadata={metadata} />}
                </Row>
            </Row>
        </Tooltip>
    );
}

export function GroupHealthCell({ healths }: { healths: LoanRowHealth[] }) {
    const hiddenCount = healths.length - 3;
    return (
        <Tooltip reverseColors title={<GroupHealthTooltip healths={healths} />}>
            <Row spacing={0.5} sx={{ alignItems: "center" }}>
                <Row sx={{ alignItems: "center" }}>
                    {healths.slice(0, 3).map((health, i) => (
                        <HealthIcon key={i} {...health} offset />
                    ))}
                </Row>
                {hiddenCount > 0 && (
                    <Text variant="body2" color="caption">
                        +{hiddenCount}
                    </Text>
                )}
            </Row>
        </Tooltip>
    );
}

function GroupHealthTooltip({ healths }: { healths: LoanRowHealth[] }) {
    return (
        <Column spacing={1} sx={{ maxWidth: 300 }}>
            {healths.map((health, i) => {
                const { loanAddress, healthRatio } = health;

                return (
                    <Row key={i} spacing={2} sx={{ alignItems: "center" }} spaceBetween>
                        <Text variant="body2" color="caption">
                            {formatAddress(loanAddress)}
                        </Text>
                        <Row spacing={0.5} sx={{ alignItems: "center" }}>
                            <Text variant="body2">{formatPercent(healthRatio)}</Text>
                            <HealthIcon {...health} />
                        </Row>
                    </Row>
                );
            })}
        </Column>
    );
}

function HealthIcon({
    ltv,
    strategyLtv,
    liquidationThreshold: strategyLqt,
    offset
}: LoanRowHealth & { offset?: boolean }) {
    const { background } = useAppPalette();
    const status = getHealthStatus(ltv, strategyLtv, strategyLqt);
    const color = useHealthColor(status);
    const iconSx = {
        height: FONT_SIZES.body1,
        width: FONT_SIZES.body1,
        color,
        ml: offset ? -1 : 0,
        stroke: offset ? `${background}` : undefined,
        strokeWidth: offset ? 1 : undefined
    };
    return <Icon type="health" sx={iconSx} />;
}

type RowActionButtonProps = { items: MenuItemProps[] } | { sections: MenuItemProps[][] };

export function RowActionsIconButton(props: RowActionButtonProps) {
    const { popoverProps, close, open } = usePopover();
    if ("items" in props) {
        return (
            <>
                <IconButton icon="more" onClick={open} textVariant="h4" />

                <PopoverWrapper
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ horizontal: "right", vertical: -SPACING }}
                    {...popoverProps}
                >
                    <MenuItems close={close} items={props.items} />
                </PopoverWrapper>
            </>
        );
    }

    return (
        <>
            <IconButton icon="more" onClick={open} textVariant="body1" />

            <PopoverWrapper
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ horizontal: "right", vertical: -SPACING }}
                {...popoverProps}
            >
                <SectionedMenuItems close={close} items={props.sections} />
            </PopoverWrapper>
        </>
    );
}

export function useViewPositionMenuSection(loan: Loan | undefined): MenuItemProps[] {
    const isMobile = useMediaQuery(MEDIA.SM.below);
    const navigate = useNavigate();
    return useMemo(() => {
        const viewPath = loan ? getLoanAssetPath(loan) : undefined;
        if (!isMobile || !viewPath) return [];
        return [{ text: "View position", icon: <Visibility />, onClick: () => navigate(viewPath) }];
    }, [isMobile, loan, navigate]);
}

export function useSharePnlMenuSection(loopPosition: LoopPositionExpanded | undefined): MenuItemProps[] {
    const { open } = useAppDialog();
    const socialShareData = useLoopSocialShareProps(loopPosition);
    return useMemo(() => {
        if (!socialShareData) return [];
        return [
            {
                text: "Share P&L",
                icon: <Icon type="share" />,
                onClick: () => open(AppDialog.SocialShare, socialShareData)
            }
        ];
    }, [open, socialShareData]);
}

export function LoanActionsButton({
    onActionClick,
    loanExpanded,
    loopPosition,
    isNestedDialogView
}: {
    onActionClick: (action: LoanAction) => void;
    loanExpanded: AbfLoanExpanded | undefined;
    loopPosition: LoopPositionExpanded | undefined;
    isNestedDialogView: boolean;
}) {
    const { close } = usePopover();
    const collateral = getZcCollateral(loanExpanded);
    const principalMetadata = loanExpanded?.principalMetadata;
    const collateralMetadata = collateral?.metadata;

    const actionGroups = useLoanActions(
        loanExpanded,
        loopPosition,
        principalMetadata,
        collateralMetadata,
        isNestedDialogView,
        undefined
    );
    const sections = useLoanActionMenuItemSections(actionGroups, async (action) => {
        close();
        await handleLoanAction(action, onActionClick);
    });

    const viewSection = useViewPositionMenuSection(loanExpanded?.loan);
    const shareSection = useSharePnlMenuSection(loopPosition);
    const allSections = [viewSection, ...sections, shareSection];
    const hasAnyItems = allSections.some((section) => section.length > 0);
    if (!hasAnyItems) return null;

    return <RowActionsIconButton sections={allSections} />;
}

export function PositionCountTag({
    count,
    variant = "body2",
    color = "body"
}: {
    count: number;
    variant?: TextVariant;
    color?: TagColor;
}) {
    return (
        <TagText borderRadius={100} textVariant={variant} color={color} sx={{ lineHeight: 1, py: "3px" }}>
            {count}
        </TagText>
    );
}

/* Group cells */
export function GroupNameCell({
    metadata,
    count
}: {
    metadata: TokenListMetadata | undefined;
    count: number | undefined;
}) {
    return (
        <Row spacing={1}>
            <TokenImage size="sm" metadata={metadata} />
            <Text variant="body1">{BsMetaUtil.getSymbol(metadata)}</Text>
            {!!count && <PositionCountTag count={count} />}
        </Row>
    );
}

export default function StrategyActionsButton({
    strategyExpanded
}: {
    strategyExpanded: StrategyExpandedInfo | undefined;
}) {
    const sections = useStrategyActionMenuItemSections(strategyExpanded);
    const hasAnyItems = sections.some((section) => section.some((item) => !item.hide));
    if (!hasAnyItems) return null;
    return <RowActionsIconButton sections={sections} />;
}
